// Чистая логика анкеты «Закрепить кандидата — Москва»:
// валидация полей + сборка текста задач и предпросмотра.
// Держим отдельно от UI, чтобы можно было прогнать self-check (см. низ файла).

import dayjs from 'dayjs';

export interface MoscowFormValues {
  // Блок A
  fullName: string;
  birthDate: string;      // YYYY-MM-DD
  phone: string;          // с маской
  passport: string;
  snils: string;
  inn: string;
  departureCity: string;
  arrivalCity: string;    // всегда «Москва»
  desiredDate: string;    // YYYY-MM-DD
  health: string;
  militaryId: '' | 'yes' | 'no';
  criminalRecord: string;
  relationNeeded: '' | 'yes' | 'no';
  // Блок B
  bFullName: string;
  bMaritalStatus: string;
  bEducation: string;
  bCriminalRecord: string;
  bPhone: string;
  bDepartment: string;
}

const digits = (s: string): string => (s ?? '').replace(/\D/g, '');

export const fmtDate = (iso: string): string => (iso ? dayjs(iso).format('DD.MM.YYYY') : '');
const today = () => dayjs().startOf('day');

// ── Валидаторы полей. Возвращают текст ошибки или '' если поле валидно. ──

export const validators = {
  fullName: (v: string) => {
    const t = (v ?? '').trim();
    if (!t) return 'Заполните это поле';
    if (t.split(/\s+/).length < 2) return 'Укажите фамилию и имя';
    if (t.length > 150) return 'Не более 150 символов';
    return '';
  },
  birthDate: (v: string) => {
    if (!v) return 'Заполните это поле';
    const d = dayjs(v);
    if (!d.isBefore(today())) return 'Дата рождения не может быть в будущем';
    const age = today().diff(d, 'year');
    if (age < 18 || age > 65) return 'Возраст должен быть от 18 до 65 лет';
    return '';
  },
  phone: (v: string) => (digits(v).length === 11 ? '' : 'Введите номер полностью'),
  passport: (v: string) => (digits(v).length === 10 ? '' : 'Введите 10 цифр'),
  snils: (v: string) => (digits(v).length === 11 ? '' : 'Введите 11 цифр'),
  inn: (v: string) => (digits(v).length === 12 ? '' : 'Введите 12 цифр'),
  departureCity: (v: string) => (v?.trim() ? '' : 'Заполните это поле'),
  desiredDate: (v: string) => {
    if (!v) return 'Заполните это поле';
    return dayjs(v).isBefore(today()) ? 'Дата не может быть раньше сегодняшней' : '';
  },
  health: (v: string) => {
    const t = (v ?? '').trim();
    if (!t) return 'Заполните это поле';
    return t.length > 255 ? 'Не более 255 символов' : '';
  },
  militaryId: (v: string) => (v === 'yes' || v === 'no' ? '' : 'Выберите значение'),
  criminalRecord: (v: string) => {
    const t = (v ?? '').trim();
    if (!t) return 'Заполните это поле';
    return t.length > 255 ? 'Не более 255 символов' : '';
  },
  relationNeeded: (v: string) => (v === 'yes' || v === 'no' ? '' : 'Выберите значение'),
  // Блок B
  notEmpty: (v: string) => ((v ?? '').trim() ? '' : 'Заполните это поле'),
  select: (v: string) => (v ? '' : 'Выберите значение'),
};

// Список проблем блока A: «N. Лейбл — текст ошибки». Пусто = блок валиден.
export const blockAIssues = (v: MoscowFormValues): string[] => {
  const checks: [string, string][] = [
    ['1. ФИО', validators.fullName(v.fullName)],
    ['2. Дата рождения', validators.birthDate(v.birthDate)],
    ['3. Телефон', validators.phone(v.phone)],
    ['4. Паспорт', validators.passport(v.passport)],
    ['5. СНИЛС', validators.snils(v.snils)],
    ['6. ИНН', validators.inn(v.inn)],
    ['7. Город отправления', validators.departureCity(v.departureCity)],
    ['8. Город прибытия', v.arrivalCity?.trim() ? '' : 'Заполните это поле'],
    ['9. Желаемая дата', validators.desiredDate(v.desiredDate)],
    ['10. Здоровье', validators.health(v.health)],
    ['11. Военный билет', validators.militaryId(v.militaryId)],
    ['12. Судимости', validators.criminalRecord(v.criminalRecord)],
    ['13. Отношение нужно', validators.relationNeeded(v.relationNeeded)],
  ];
  return checks.filter(([, err]) => err).map(([label, err]) => `${label} — ${err}`);
};

export const blockBIssues = (v: MoscowFormValues): string[] => {
  const checks: [string, string][] = [
    ['1. ФИО', validators.notEmpty(v.bFullName)],
    ['2. Семейное положение', validators.notEmpty(v.bMaritalStatus)],
    ['3. Образование', validators.notEmpty(v.bEducation)],
    ['4. Судимости', validators.notEmpty(v.bCriminalRecord)],
    ['5. Телефон', validators.phone(v.bPhone)],
    ['6. На кого отношение', validators.select(v.bDepartment)],
  ];
  return checks.filter(([, err]) => err).map(([label, err]) => `${label} — ${err}`);
};

export const isBlockAComplete = (v: MoscowFormValues): boolean =>
  !validators.fullName(v.fullName) &&
  !validators.birthDate(v.birthDate) &&
  !validators.phone(v.phone) &&
  !validators.passport(v.passport) &&
  !validators.snils(v.snils) &&
  !validators.inn(v.inn) &&
  !validators.departureCity(v.departureCity) &&
  !!v.arrivalCity?.trim() &&
  !validators.desiredDate(v.desiredDate) &&
  !validators.health(v.health) &&
  !validators.militaryId(v.militaryId) &&
  !validators.criminalRecord(v.criminalRecord) &&
  !validators.relationNeeded(v.relationNeeded);

export const isBlockBComplete = (v: MoscowFormValues): boolean =>
  !validators.notEmpty(v.bFullName) &&
  !validators.notEmpty(v.bMaritalStatus) &&
  !validators.notEmpty(v.bEducation) &&
  !validators.notEmpty(v.bCriminalRecord) &&
  !validators.phone(v.bPhone) &&
  !validators.select(v.bDepartment);

const yesNo = (v: string) => (v === 'yes' ? 'да' : v === 'no' ? 'нет' : '—');
const orDash = (s?: string) => (s?.trim() ? s.trim() : '—');

// Текст задачи на витрину (пп. 1–13). Лейблы — как в макете предпросмотра.
// Незаполненное показываем как «—», чтобы предпросмотр обновлялся сразу по мере ввода.
export const buildShowcaseText = (v: MoscowFormValues): string =>
  'Заявка на витрину — Москва. ' +
  [
    `1. ФИО: ${orDash(v.fullName)}`,
    `2. Дата рождения: ${fmtDate(v.birthDate) || '—'}`,
    `3. Номер телефона: ${orDash(v.phone)}`,
    `4. Паспорт: ${orDash(v.passport)}`,
    `5. СНИЛС: ${orDash(v.snils)}`,
    `6. ИНН: ${orDash(v.inn)}`,
    `7. Город отправления: ${orDash(v.departureCity)}`,
    `8. Город прибытия: ${orDash(v.arrivalCity)}`,
    `9. Желаемая дата: ${fmtDate(v.desiredDate) || '—'}`,
    `10. Здоровье: ${orDash(v.health)}`,
    `11. Военный билет: ${yesNo(v.militaryId)}`,
    `12. Судимости: ${orDash(v.criminalRecord)}`,
    `13. Отношение нужно: ${yesNo(v.relationNeeded)}`,
  ].join('; ') + '.';

// Текст задачи на отношение (пп. 1–6).
export const buildRelationText = (v: MoscowFormValues): string =>
  'Заявка на отношение — Новая Москва. ' +
  [
    `1. ФИО: ${orDash(v.bFullName)}`,
    `2. Семейное положение: ${orDash(v.bMaritalStatus)}`,
    `3. Образование: ${orDash(v.bEducation)}`,
    `4. Судимости: ${orDash(v.bCriminalRecord)}`,
    `5. Номер телефона: ${orDash(v.bPhone)}`,
    `6. На кого отношение: ${orDash(v.bDepartment)}`,
  ].join('; ') + '.';

export interface Preview {
  ready: boolean;      // всё заполнено, можно создавать
  headline: string;    // строка статуса сверху
  showcase: string;    // живой текст задачи на витрину (пп. 1–13)
  relation: string;    // живой текст задачи на отношение (пусто, если не нужен)
  issues: string[];    // что осталось заполнить
  tasks: string[];     // имена задач, которые реально уйдут в CRM (пусто, пока не ready)
}

export const buildPreview = (v: MoscowFormValues): Preview => {
  const aOk = isBlockAComplete(v);
  const showcase = buildShowcaseText(v);
  const needRelation = v.relationNeeded === 'yes';
  const relation = needRelation ? buildRelationText(v) : '';

  const issues = [
    ...blockAIssues(v),
    ...(needRelation ? blockBIssues(v) : []),
  ];

  if (!aOk) {
    return { ready: false, headline: 'Черновик — заполните оставшиеся пункты анкеты на витрину.', showcase, relation, issues, tasks: [] };
  }
  if (needRelation && !isBlockBComplete(v)) {
    return { ready: false, headline: 'Черновик — заполните оставшиеся пункты анкеты на отношение.', showcase, relation, issues, tasks: [] };
  }
  const tasks = needRelation ? [showcase, relation] : [showcase];
  const headline = needRelation ? 'Будет создано 2 задачи.' : 'Будет создана 1 задача.';
  return { ready: true, headline, showcase, relation, issues: [], tasks };
};

// ── self-check (ponytail): один прогон, ловит поломку сборки текста/валидации ──
// Запуск: npx tsx src/features/pin-moscow-form/model/preview.ts
if (typeof require !== 'undefined' && require.main === module) {
  const base: MoscowFormValues = {
    fullName: 'Климов Александр', birthDate: '1990-08-20', phone: '+7 (950) 251-89-43',
    passport: '1234 567890', snils: '123-456-789 00', inn: '123456789012',
    departureCity: 'Архангельск', arrivalCity: 'Москва', desiredDate: '2099-09-01',
    health: 'Здоров', militaryId: 'no', criminalRecord: 'Нет', relationNeeded: 'no',
    bFullName: '', bMaritalStatus: '', bEducation: '', bCriminalRecord: '', bPhone: '', bDepartment: '',
  };
  const assert = (c: boolean, m: string) => { if (!c) { throw new Error('FAIL: ' + m); } };

  assert(buildPreview(base).ready && buildPreview(base).tasks.length === 1, 'A полный, отношение нет → 1 задача');
  assert(buildPreview(base).showcase.includes('13. Отношение нужно: нет'), 'п.13 = нет');
  assert(buildPreview(base).showcase.includes('11. Военный билет: нет'), 'военный билет: нет');

  const empty = { ...base, fullName: '' };
  assert(!buildPreview(empty).ready && buildPreview(empty).tasks.length === 0, 'A не полон → не ready, 0 задач');
  assert(buildPreview(empty).showcase.includes('1. ФИО: —'), 'пустое поле в живом превью показано как —');
  assert(buildPreview(empty).showcase.includes('2. Дата рождения: 20.08.1990'), 'заполненное поле видно сразу');
  assert(buildPreview(empty).issues.some(i => i.startsWith('1. ФИО')), 'issues называет незаполненный пункт');

  const rel = { ...base, relationNeeded: 'yes' as const };
  assert(!buildPreview(rel).ready && buildPreview(rel).relation !== '', 'отношение да, B пуст → не ready, но текст B живой');

  const relFull = {
    ...rel, bFullName: 'Климов Александр', bMaritalStatus: 'Не женат', bEducation: 'Высшее',
    bCriminalRecord: 'Нет', bPhone: '+7 (950) 251-89-43', bDepartment: 'Подразделение А',
  };
  assert(buildPreview(relFull).ready && buildPreview(relFull).tasks.length === 2, 'оба блока → ready, 2 задачи');
  assert(buildPreview(relFull).showcase.includes('13. Отношение нужно: да'), 'п.13 = да');

  assert(validators.passport('123') === 'Введите 10 цифр', 'паспорт < 10');
  assert(validators.inn('123456789012') === '', 'инн 12 цифр ок');
  assert(validators.birthDate('2099-01-01') !== '', 'дата рождения в будущем невалидна');
  assert(validators.fullName('Иван') !== '', 'одно слово невалидно');

  // eslint-disable-next-line no-console
  console.log('preview.ts self-check OK');
}
